import { MapPinIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type FindEntitiesNearLocationArgs,
  type FindEntitiesNearLocationResult,
  formatDistance,
} from '@/features/agent/tools/find-entities-near-location'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface FindEntitiesNearLocationViewProps {
  args: FindEntitiesNearLocationArgs
  parsed: FindEntitiesNearLocationResult | null
  running: boolean
  errored: boolean
  /** Resolved action footer, supplied by the connected tool. Pure stories
   *  pass a mock `<MapCardActions>` here to render the footer. */
  actions?: ReactNode
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

/** Pure presentation for a `find_entities_near_location` tool-result card. */
export function FindEntitiesNearLocationView({
  args,
  parsed,
  running,
  errored,
  actions,
  defaultOpen = false,
}: FindEntitiesNearLocationViewProps) {
  const { t } = useTranslation()

  const anchors = parsed?.anchors ?? []
  const hasBody = !running && (errored || parsed?.status === 'ok' || parsed?.status === 'ambiguous')
  // `ambiguous` keeps auto-open: it asks the user to pick a location, unlike a failure.
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, errored)

  if (!running && parsed?.status === 'empty') return null

  const locationName = parsed?.location?.name ?? args.location
  const anchorCount = anchors.length

  const headerStatus: ToolCallStatus = running
    ? 'running'
    : errored || parsed?.status === 'ambiguous'
      ? 'error'
      : 'complete'

  const description = running ? (
    <>
      Searching near <span className="mx-1.5">·</span>
      <span className="text-foreground">{locationName}</span>
    </>
  ) : parsed?.status === 'ambiguous' ? (
    'Ambiguous location'
  ) : errored ? (
    'Failed'
  ) : (
    <>
      {anchorCount === 1 ? '1 location' : `${anchorCount} locations`}
      {parsed?.radius_km != null && (
        <>
          <span className="mx-1.5">·</span>within {parsed.radius_km} km
        </>
      )}
      <span className="mx-1.5">·</span>
      <span className="text-foreground">{locationName}</span>
    </>
  )

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={MapPinIcon}
        toolName="find_entities_near_location"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />

      {open && hasBody && (
        <div className="border-t border-border">
          {(parsed?.status === 'error' || parsed?.status === 'disabled') && (
            <p className="whitespace-pre-wrap p-3 text-xs text-muted-foreground">
              {parsed.error ?? 'Unknown error'}
            </p>
          )}

          {parsed?.status === 'ambiguous' && (
            <div className="flex flex-col">
              <p className="border-b border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Multiple places match — ask which one is meant:
              </p>
              <ul className="divide-y divide-border">
                {(parsed.candidates ?? []).map((c, i) => (
                  <li key={i} className="px-3 py-2 text-xs">
                    <p className="truncate font-medium text-foreground">{c.display}</p>
                    <p className="text-muted-foreground">
                      {[c.city, c.country].filter(Boolean).join(', ')}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {parsed?.status === 'ok' && (
            <div className="flex flex-col">
              {anchors.length > 0 ? (
                <ul className="divide-y divide-border">
                  {anchors.slice(0, 10).map(anchor => {
                    const dist = formatDistance(anchor.distance_meters)
                    const neighborCount = anchor.neighbors?.length ?? 0
                    return (
                      <li
                        key={anchor.anchor_id}
                        className="flex items-start gap-2.5 px-3 py-2 text-xs"
                      >
                        <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-success" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-foreground">{anchor.name}</p>
                          <p className="text-muted-foreground">
                            {dist}
                            {dist && neighborCount > 0 && <span className="mx-1.5">·</span>}
                            {neighborCount > 0 &&
                              (neighborCount === 1
                                ? t('chat.nearLocation.oneNeighbor')
                                : t('chat.nearLocation.nNeighbors', { count: neighborCount }))}
                          </p>
                        </div>
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          {anchor.type}
                        </span>
                      </li>
                    )
                  })}
                  {anchors.length > 10 && (
                    <li className="px-3 py-2 text-xs text-muted-foreground">
                      {t('chat.nearLocation.moreLocations', { count: anchors.length - 10 })}
                    </li>
                  )}
                </ul>
              ) : (
                <p className="p-3 text-xs text-muted-foreground">{t('chat.nearLocation.none')}</p>
              )}

              {anchorCount > 0 && actions && (
                <div className="border-t border-border px-3 py-2">{actions}</div>
              )}
            </div>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}
